import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  List,
  Switch,
  Tab,
  Tabs,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  IconButton,
  InputAdornment,
  Autocomplete,
  Chip,
  alpha,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Grid,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WorkIcon from '@mui/icons-material/Work';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CreateIcon from '@mui/icons-material/Create';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { searchUsers, sendInvitation, createCrewMember, removeInvitation, removeCrewMember, toggleAdminStatus, getProjectCrew, updateCrewMember, getPermissionsRegistry, getRoleTemplates, updateMemberPermissions, updateMemberAccess } from '../services/crewService';
import { API_URL } from '../config';
import CrewCard from './CrewCard';
import { formatDepartmentLabel, formatRoleLabel } from '../utils/labels';

// Department-specific roles mapping
const DEPARTMENT_ROLES = {
  'direction': [
    'director',
    'first-assistant-director',
    'second-assistant-director',
    'second-second-ad',
    'script-supervisor',
    'additional-assistant-director',
    'director-pa'
  ],
  'production': [
    'executive-producer',
    'producer',
    'line-producer',
    'unit-production-manager',
    'production-supervisor',
    'production-coordinator',
    'production-secretary',
    'production-accountant',
    'production-assistant',
    'set-pa'
  ],
  'camera': [
    'director-of-photography',
    'cinematographer',
    'camera-operator',
    'first-assistant-camera',
    'second-assistant-camera',
    'loader',
    'camera-utility',
    'dit',
    'steadicam-operator',
    'camera-pa'
  ],
  'electric': [
    'gaffer',
    'best-boy-electric',
    'electric',
    'genny-operator',
    'board-operator',
    'rigging-electric',
    'electric-utility',
    'electric-pa'
  ],
  'grip': [
    'key-grip',
    'best-boy-grip',
    'grip',
    'dolly-grip',
    'rigging-grip',
    'grip-utility',
    'grip-pa'
  ],
  'sound': [
    'production-sound-mixer',
    'sound-supervisor',
    'boom-operator',
    'sound-utility',
    'sound-pa'
  ],
  'art': [
    'production-designer',
    'art-director',
    'set-decorator',
    'prop-master',
    'set-dresser',
    'art-pa'
  ],
  'makeup': [
    'makeup-department-head',
    'key-makeup-artist',
    'makeup-artist',
    'special-effects-makeup',
    'makeup-pa'
  ],
  'hair': [
    'hair-department-head',
    'key-hair-stylist',
    'hair-stylist',
    'hair-pa'
  ],
  'costume': [
    'costume-designer',
    'assistant-costume-designer',
    'costume-supervisor',
    'set-costumer',
    'costume-pa'
  ],
  'post-production': [
    'post-supervisor',
    'editor',
    'colorist'
  ],
  'vfx': [
    'vfx-supervisor',
    'vfx-artist'
  ],
  'location': [
    'location-manager',
    'location-scout'
  ],
  'transportation': [
    'transportation-coordinator',
    'transportation-captain',
    'driver',
    'transportation-pa'
  ],
  'craft-services': [
    'craft-services-lead',
    'craft-services-assistant'
  ],
  'catering': [
    'caterer',
    'catering-assistant'
  ],
  'construction': [
    'construction-coordinator',
    'construction-foreman',
    'carpenter',
    'scenic',
    'construction-pa'
  ],
  'greens': [
    'greensmaster',
    'assistant-greens',
    'greens-person'
  ],
  'stunts': [
    'stunt-coordinator',
    'stunt-performer',
    'stunt-rigger'
  ],
  'special-effects': [
    'sfx-supervisor',
    'sfx-technician',
    'pyrotechnician'
  ],
  'accounting': [
    'assistant-accountant',
    'payroll-accountant'
  ],
  'security': [
    'security-coordinator',
    'security-guard'
  ],
  'medical': [
    'set-medic'
  ],
  'publicity': [
    'publicist',
    'unit-publicist'
  ],
  'other': ['other']
};

// Valid departments
const VALID_DEPARTMENTS = [
  'direction',
  'production',
  'camera',
  'electric',
  'grip',
  'sound',
  'art',
  'makeup',
  'hair',
  'costume',
  'post-production',
  'vfx',
  'location',
  'transportation',
  'craft-services',
  'catering',
  'construction',
  'greens',
  'stunts',
  'special-effects',
  'accounting',
  'security',
  'medical',
  'publicity',
  'other'
];

const CrewManagement = ({ projectId, crewMembers: initialCrewMembers, onAddCrewMember, onDeleteCrewMember }) => {
  const theme = useTheme();
  const [crewMembers, setCrewMembers] = useState(initialCrewMembers || []);
  const [openCrewDialog, setOpenCrewDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [newCrewMember, setNewCrewMember] = useState({
    name: '',
    email: '',
    role: '',
    department: ''
  });
  const [isSearching, setIsSearching] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [availableRoles, setAvailableRoles] = useState(DEPARTMENT_ROLES.other);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  // Permissions dialog state
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionsRegistry, setPermissionsRegistry] = useState({ groups: [], permissions: [] });
  const [roleTemplates, setRoleTemplates] = useState({});
  const [selectedMemberForPerms, setSelectedMemberForPerms] = useState(null);
  const [permsDraft, setPermsDraft] = useState({});
  const [adminDraft, setAdminDraft] = useState(false);
  const [initialAdminDraft, setInitialAdminDraft] = useState(false); // Track initial admin state
  const [permSearch, setPermSearch] = useState('');

  // Helpers to robustly compare IDs
  const getId = (val) => {
    if (!val) return undefined;
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val._id || val.id;
    return undefined;
  };

  // When Admin is toggled, reflect changes in the permission switches immediately
  useEffect(() => {
    // Only run when dialog is open
    if (!permissionsDialogOpen) return;
    // Only apply template if admin toggle was actually changed by user, not on initial load
    if (adminDraft === initialAdminDraft) return;
    
    // Ensure registry loaded for fallback template
    const keys = (permissionsRegistry?.permissions || []).map(p => p.key);
    const applyAdmin = () => {
      // Prefer server-provided admin template
      const tmpl = roleTemplates?.admin;
      if (tmpl && typeof tmpl === 'object') {
        // Replace all permissions with admin template
        setPermsDraft({ ...tmpl });
        return;
      }
      // Fallback: enable all known permission keys when admin is on
      if (keys.length > 0) {
        const allEnabled = {};
        keys.forEach(k => { allEnabled[k] = true; });
        setPermsDraft(allEnabled);
      }
    };

    const applyCrewOrBaseline = () => {
      // Prefer a 'crew' baseline template if available
      const crewTmpl = roleTemplates?.crew;
      if (crewTmpl && typeof crewTmpl === 'object') {
        // Replace all permissions with crew template (clears admin permissions)
        setPermsDraft({ ...crewTmpl });
        return;
      }
      // Fallback: clear all permissions
      const allDisabled = {};
      keys.forEach(k => { allDisabled[k] = false; });
      setPermsDraft(allDisabled);
    };

    if (adminDraft) applyAdmin(); else applyCrewOrBaseline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminDraft, permissionsDialogOpen, roleTemplates, permissionsRegistry]);

  // Normalize a permissions value into an object map: { key: true|false }
  // Accepts array of strings or object; returns a plain object suitable for the dialog
  const normalizePermissions = (perms) => {
    try {
      if (Array.isArray(perms)) {
        return perms.reduce((acc, key) => {
          if (typeof key === 'string' && key.trim()) acc[key] = true;
          return acc;
        }, {});
      }
      if (perms && typeof perms === 'object') {
        return { ...perms };
      }
      return {};
    } catch {
      return {};
    }
  };

  // Open Manage Permissions dialog
  const handleManagePermissions = async (member) => {
    try {
      setSelectedMemberForPerms(member);
      // Initialize draft from member.permissions (object) or empty
      setPermsDraft(normalizePermissions(member.permissions));
      // Initialize admin draft from effective admin
      const isAdminValue = effectiveIsAdmin(member);
      setAdminDraft(isAdminValue);
      setInitialAdminDraft(isAdminValue); // Store initial value to detect changes
      // Lazy-load registry and templates if not loaded
      if (!permissionsRegistry.permissions?.length) {
        try {
          const [reg, tmpl] = await Promise.all([
            getPermissionsRegistry(),
            getRoleTemplates(),
          ]);
          setPermissionsRegistry(reg || { groups: [], permissions: [] });
          setRoleTemplates((tmpl && tmpl.templates) || {});
        } catch (e) {
          console.error('Failed to load permissions metadata:', e);
        }
      }
      setPermissionsDialogOpen(true);
    } catch (e) {
      console.error('Error opening permissions dialog:', e);
    }
  };

  // Apply a role template - replaces all permissions
  const applyRoleTemplate = (templateKey) => {
    const tmpl = roleTemplates?.[templateKey];
    if (!tmpl) return;
    // Replace all permissions with the template (don't merge)
    setPermsDraft({ ...tmpl });
  };

  // Toggle single permission in draft
  const togglePermDraft = (key, value) => {
    setPermsDraft((prev) => ({ ...prev, [key]: value }));
  };

  // Save permissions to server
  const saveMemberPermissions = async () => {
    if (!selectedMemberForPerms) return;
    try {
      const memberId = selectedMemberForPerms._id;
      const initialIsAdmin = effectiveIsAdmin(selectedMemberForPerms);
      // Build payload constrained to known registry keys to ensure persistence
      const allowedKeys = (permissionsRegistry?.permissions || []).map(p => p.key);
      let permissionsDelta = {};
      if (allowedKeys.length > 0) {
        // Send ALL permissions, setting missing ones to false
        permissionsDelta = allowedKeys.reduce((acc, k) => {
          // If key exists in permsDraft, use its value; otherwise set to false
          acc[k] = Object.prototype.hasOwnProperty.call(permsDraft || {}, k) 
            ? !!permsDraft[k] 
            : false;
          return acc;
        }, {});
      } else {
        // Fallback to delta if registry not loaded
        const initialPerms = selectedMemberForPerms.permissions || {};
        permissionsDelta = Object.fromEntries(
          Object.entries(permsDraft || {}).filter(([k, v]) => initialPerms[k] !== v)
        );
      }

      await updateMemberAccess(projectId, memberId, {
        isAdmin: adminDraft,
        initialIsAdmin,
        permissionsDelta
      });

      // Refetch to reconcile with server and avoid stale local state
      await fetchCrewMembers();
      setNotification({ open: true, message: 'Role & permissions updated', severity: 'success' });
      setPermissionsDialogOpen(false);
      setSelectedMemberForPerms(null);
    } catch (e) {
      console.error('Failed to update permissions:', e);
      setNotification({ open: true, message: e.message || 'Failed to update permissions', severity: 'error' });
    }
  };
  const isOwner = (member) => {
    const ownerId = getId(projectDetails?.owner);
    const memberUserId = getId(member?.user);
    return ownerId && memberUserId && String(ownerId) === String(memberUserId);
  };

  // Heuristic: derive effective admin if server omits boolean isAdmin
  const effectiveIsAdmin = (member) => {
    if (!member) return false;
    if (member.status === 'pending') return false;
    if (isOwner(member)) return true;
    // Coerce boolean-like values for isAdmin
    if (member.isAdmin === true || member.isAdmin === 'true' || member.isAdmin === 1) return true;
    if (member.role && typeof member.role === 'string' && member.role.toLowerCase() === 'admin') return true;
    // Support both array and object style permissions
    if (Array.isArray(member.permissions) && member.permissions.includes('project.manage')) return true;
    if (member.permissions && typeof member.permissions === 'object' && member.permissions.manageProject === true) return true;
    return false;
  };

  // Update local crew members when prop changes
  useEffect(() => {
    if (initialCrewMembers) {
      setCrewMembers(initialCrewMembers);
    }
  }, [initialCrewMembers]);
  
  // Track recent admin toggles to reconcile if server omits isAdmin momentarily
  const recentAdminTogglesRef = useRef({}); // { [memberId]: { value: boolean, at: number } }

  // Load crew members from the server when the component mounts (guarded for StrictMode)
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!projectId) return;
    if (didInitRef.current) return; // StrictMode dev guard to prevent duplicate fetches
    didInitRef.current = true;
    (async () => {
      // Fetch project details first so we can mark owner as admin, then fetch crew
      await fetchProjectDetails();
      await fetchCrewMembers();
    })();
  }, [projectId]);
  
  // State to store project details
  const [projectDetails, setProjectDetails] = useState(null);

  const fetchProjectDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch project details');
      }
      
      const data = await response.json();
      console.log('Project details:', data);
      setProjectDetails(data);
      return data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      return null;
    }
  };

  const fetchCrewMembers = async () => {
    try {
      const data = await getProjectCrew(projectId);
      console.log('Fetched crew members in component:', data);
      
      // If we have project details, check for the creator and set admin status
      if (projectDetails && projectDetails.owner) {
        const updatedData = data.map(member => {
          // If this crew member is the project owner, ensure they have admin privileges
          if (member.user && member.user._id === projectDetails.owner._id) {
            return { ...member, isAdmin: true };
          }
          return member;
        });
        setCrewMembers(updatedData.map(m => ({
          ...m,
          // Ensure permissions is an object for consistent UI behavior
          permissions: normalizePermissions(m.permissions)
        })));
        return updatedData;
      } else {
        const normalized = (data || []).map(m => ({
          ...m,
          permissions: normalizePermissions(m.permissions)
        }));
        setCrewMembers(normalized);
        return normalized;
      }
    } catch (error) {
      console.error('Error fetching crew members:', error);
      setNotification({
        open: true,
        message: 'Failed to refresh crew members list',
        severity: 'error'
      });
      return [];
    }
  };

  const validateForm = () => {
    const errors = {};
    if (dialogTab === 0) {
      if (!selectedUser) {
        errors.user = 'Please select a user';
      }
    } else {
      if (!newCrewMember.name) {
        errors.name = 'Name is required';
      }
      if (!newCrewMember.email) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(newCrewMember.email)) {
        errors.email = 'Invalid email format';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSearch = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchUsers(query);
      setSearchResults(results || []); // Ensure we always have an array
    } catch (error) {
      console.error('Error searching users:', error);
      setNotification({
        open: true,
        message: 'Error searching users',
        severity: 'error'
      });
      setSearchResults([]); // Set empty array on error
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddExistingUser = async () => {
    if (!selectedUser) {
      setNotification({
        open: true,
        message: 'Please select a valid user',
        severity: 'error'
      });
      return;
    }

    // Validate role and department
    const validationErrors = {};
    if (!newCrewMember.department) {
      validationErrors.department = 'Department is required';
    }
    if (!newCrewMember.role) {
      validationErrors.role = 'Role is required';
    }
    if (newCrewMember.department && newCrewMember.role) {
      const departmentRoles = DEPARTMENT_ROLES[newCrewMember.department] || [];
      if (!departmentRoles.includes(newCrewMember.role)) {
        validationErrors.role = 'Selected role is not valid for the chosen department';
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Unify flow: always send an invitation for existing users
      const invitation = await sendInvitation(projectId, {
        name: selectedUser.name,
        email: selectedUser.email,
        role: newCrewMember.role,
        department: newCrewMember.department,
        userId: selectedUser._id
      });

      if (invitation) {
        const newInvitation = {
          _id: invitation._id || invitation.invitation?._id,
          user: {
            _id: selectedUser._id,
            name: selectedUser.name,
            email: selectedUser.email
          },
          role: newCrewMember.role,
          department: newCrewMember.department,
          status: 'pending',
          createdAt: new Date(),
          token: invitation.token || invitation.invitation?.token
        };

        setCrewMembers(prev => [...prev, newInvitation]);
        onAddCrewMember && onAddCrewMember(newInvitation);

        handleCloseDialog();
        setNotification({
          open: true,
          message: 'Invitation sent to existing user successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error adding crew member:', error);
      
      if (error.response?.data?.errors) {
        const validationErrors = {};
        error.response.data.errors.forEach(err => {
          if (typeof err === 'string') {
            if (err.toLowerCase().includes('department')) {
              validationErrors.department = err;
            } else if (err.toLowerCase().includes('role')) {
              validationErrors.role = err;
            } else {
              setNotification({
                open: true,
                message: err,
                severity: 'error'
              });
            }
          }
        });
        setFormErrors(validationErrors);
      } else {
        setNotification({
          open: true,
          message: error.message || 'Failed to add crew member',
          severity: 'error'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCrewMember = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const role = newCrewMember.role;
      const department = newCrewMember.department;

      if (!role && department) {
        setFormErrors({ role: 'Role is required when department is specified' });
        return;
      }

      if (department && !role) {
        setFormErrors({ role: 'Role is required when department is specified' });
        return;
      }

      // Always send an invitation, whether it's an existing user or new user
      const data = {
        name: dialogTab === 0 ? selectedUser.name : newCrewMember.name?.trim(),
        email: dialogTab === 0 ? selectedUser.email : newCrewMember.email?.trim(),
        role,
        department,
        userId: dialogTab === 0 ? selectedUser._id : undefined // Include user ID for existing users
      };

      console.log('Sending invitation:', data);

      // Send invitation
      const invitation = await sendInvitation(projectId, data);

      if (invitation) {
        const invitationPayload = invitation.invitation || invitation;

        if (!invitationPayload?._id) {
          throw new Error('Invitation response missing ID');
        }

        const newInvitation = {
          _id: invitationPayload._id,
          user: {
            _id: data.userId, // Include user ID for existing users
            name: data.name,
            email: data.email
          },
          role: invitationPayload.role || data.role,
          department: invitationPayload.department || data.department,
          status: invitationPayload.status || 'pending',
          createdAt: invitationPayload.createdAt ? new Date(invitationPayload.createdAt) : new Date(),
          token: invitationPayload.token,
          project: invitationPayload.project,
          invitationId: invitationPayload._id
        };

        setCrewMembers(prev => [...prev, newInvitation]);
        onAddCrewMember?.(newInvitation);
        handleCloseDialog();
        setNotification({
          open: true,
          message: data.userId
            ? 'Invitation sent to existing user successfully'
            : 'Invitation sent successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error adding crew member:', error);
      
      if (error.response?.data?.errors) {
        const validationErrors = {};
        error.response.data.errors.forEach(err => {
          if (typeof err === 'string') {
            if (err.toLowerCase().includes('department')) {
              validationErrors.department = err;
            } else if (err.toLowerCase().includes('role')) {
              validationErrors.role = err;
            } else {
              setNotification({
                open: true,
                message: err,
                severity: 'error'
              });
            }
          }
        });
        setFormErrors(validationErrors);
      } else {
        setNotification({
          open: true,
          message: error.message || 'Failed to add crew member',
          severity: 'error'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCrewMember = async (memberId, member) => {
    try {
      // Check if this is an invitation by checking if it has a token or createdAt
      const isInvitation = member.token || (member.createdAt && !member.joinedAt);

      // Hard-stop if attempt to remove the project owner
      if (!isInvitation && isOwner(member)) {
        setNotification({
          open: true,
          message: 'The project owner cannot be removed from the crew.',
          severity: 'warning'
        });
        return;
      }
      
      if (isInvitation) {
        // If it's a pending invitation, use removeInvitation
        await removeInvitation(projectId, memberId);
        setNotification({
          open: true,
          message: 'Invitation cancelled successfully',
          severity: 'success'
        });
      } else {
        // If it's a crew member, use removeCrewMember
        await removeCrewMember(projectId, memberId);
        setNotification({
          open: true,
          message: 'Crew member removed successfully',
          severity: 'success'
        });
      }
      
      // Remove the member from local state
      setCrewMembers(prev => prev.filter(m => m._id !== memberId));
      
      // Only notify parent component if the deletion was successful
      if (onDeleteCrewMember) {
        onDeleteCrewMember(memberId, isInvitation);
      }
    } catch (error) {
      console.error('Error removing crew member:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to remove crew member',
        severity: 'error'
      });
      
      // Don't remove from local state or notify parent on error
      throw error; // Re-throw so parent can handle if needed
    }
  };

  const handleToggleAdmin = async (memberId, isAdmin) => {
    try {
      // Find the crew member
      const memberIndex = crewMembers.findIndex(m => m._id === memberId);
      if (memberIndex === -1) {
        throw new Error('Crew member not found');
      }
      
      // Check if this member is the project creator/owner
      const isProjectOwner = projectDetails && 
                            projectDetails.owner && 
                            crewMembers[memberIndex].user && 
                            crewMembers[memberIndex].user._id === projectDetails.owner._id;
      
      // Prevent removing admin status from project owner
      if (isProjectOwner && !isAdmin) {
        setNotification({
          open: true,
          message: 'Cannot remove admin privileges from the project creator',
          severity: 'warning'
        });
        return;
      }

      // Prevent demoting the last remaining admin
      const currentAdminCount = crewMembers.reduce((acc, m) => {
        // Exclude pending members from admin count
        if (m.status === 'pending') return acc;
        return acc + (effectiveIsAdmin(m) ? 1 : 0);
      }, 0);
      const targetIsCurrentlyAdmin = effectiveIsAdmin(crewMembers[memberIndex]);
      if (!isAdmin && targetIsCurrentlyAdmin && currentAdminCount <= 1) {
        setNotification({
          open: true,
          message: 'Cannot remove admin privileges: at least one admin is required.',
          severity: 'warning'
        });
        return;
      }

      // Update the local state first for immediate feedback
      const updatedCrewMembers = [...crewMembers];
      updatedCrewMembers[memberIndex] = {
        ...updatedCrewMembers[memberIndex],
        isAdmin: isAdmin
      };
      setCrewMembers(updatedCrewMembers);

      // Update admin status on the server
      const response = await toggleAdminStatus(projectId, memberId, isAdmin, { reason: 'user_action' });
      console.log('Server response for admin toggle:', response);
      // Record recent toggle intent (5s window)
      recentAdminTogglesRef.current[memberId] = { value: isAdmin, at: Date.now() };
      
      // Re-fetch from server to verify persistence and reconcile UI (force bypass cache)
      try {
        const latestCrew = await getProjectCrew(projectId, { force: true });
        // Reconcile: if server omits isAdmin for the toggled member, apply recent override
        const reconciled = latestCrew.map(m => {
          const id = String(getId(m));
          const rec = recentAdminTogglesRef.current[id];
          // Apply short-lived override regardless of current value to bridge backend propagation
          if (id === String(memberId) && rec && Date.now() - rec.at < 5000) {
            return { ...m, isAdmin: rec.value };
          }
          return m;
        });
        setCrewMembers(reconciled);
        const serverMember = reconciled.find(m => String(getId(m)) === String(memberId));
        if (serverMember) {
          const serverEffective = effectiveIsAdmin(serverMember);
          if (serverEffective !== isAdmin) {
            console.warn('Server did not reflect admin change as expected:', {
              memberId,
              expected: isAdmin,
              serverEffective,
              server_isAdmin: serverMember?.isAdmin,
              server_status: serverMember?.status,
              server_role: serverMember?.role,
              server_permissions: serverMember?.permissions,
              server_id: getId(serverMember)
            });
          }
        }
      } catch (refreshErr) {
        console.warn('Post-toggle verification fetch failed:', refreshErr);
      }

      setNotification({
        open: true,
        message: response.message || `Admin ${isAdmin ? 'privileges granted' : 'privileges revoked'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling admin status:', error);
      
      // If there's an error, revert the local state change
      if (error.status === 409 || error.status === 412 || error?.response?.status === 409 || error?.response?.status === 412) {
        // Conflict or precondition failed - refetch latest server state
        await fetchCrewMembers();
      } else {
        fetchCrewMembers();
      }
      
      setNotification({
        open: true,
        message: error.message || 'Failed to update admin status',
        severity: 'error'
      });
    }
  };

  const handleUpdateCrewMember = async (updatedMember) => {
    try {
      // Validate the updated data
      if (!updatedMember.role || !updatedMember.department) {
        throw new Error('Role and department are required');
      }

      // Check if the role is valid for the department
      const departmentRoles = DEPARTMENT_ROLES[updatedMember.department] || [];
      if (!departmentRoles.includes(updatedMember.role)) {
        throw new Error('Selected role is not valid for the chosen department');
      }

      // Update the crew member on the server
      const response = await updateCrewMember(projectId, updatedMember._id, {
        role: updatedMember.role,
        department: updatedMember.department
      });

      // Update the local state with the new data
      setCrewMembers(prev => prev.map(member => 
        member._id === updatedMember._id 
          ? { ...member, role: updatedMember.role, department: updatedMember.department }
          : member
      ));

      setNotification({
        open: true,
        message: 'Crew member position updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating crew member:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to update crew member position',
        severity: 'error'
      });
      // If crew member not found, refresh the list to remove stale entries
      if (error.message?.includes('not found')) {
        fetchCrewMembers();
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenCrewDialog(false);
    setDialogTab(0);
    setSelectedUser(null);
    setNewCrewMember({
      name: '',
      email: '',
      role: '',
      department: ''
    });
    setFormErrors({});
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleInputChange = (field, value) => {
    setNewCrewMember(prev => ({ ...prev, [field]: value }));
  };

  const handleDepartmentChange = (event) => {
    const department = event.target.value;
    setNewCrewMember(prev => ({ ...prev, department, role: '' })); // Reset role when department changes
    setAvailableRoles(DEPARTMENT_ROLES[department] || DEPARTMENT_ROLES.other);
  };

  useEffect(() => {
    if (selectedUser) {
      setNewCrewMember(prev => ({
        ...prev,
        name: selectedUser.name,
        email: selectedUser.email
      }));
    }
  }, [selectedUser]);

  return (
    <Box>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          sx={{ width: '100%', borderRadius: 1 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        py: 1,
        px: 2
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <GroupsIcon sx={{ color: 'primary.main', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" sx={{
            fontWeight: 600,
            color: (theme) => theme.palette.text.primary
          }}>
            Crew
          </Typography>
          <Tooltip title="Add crew member" arrow>
            <IconButton
              onClick={() => setOpenCrewDialog(true)}
              size="small"
              sx={{
                color: (theme) => theme.palette.primary.main
              }}
            >
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      
      {/* Department Filter - inline, discreet */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 2 }}>
        <FormControl 
          variant="outlined" 
          size="small" 
          sx={{ minWidth: 120 }}
        >
          <Select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            displayEmpty
            renderValue={(v) => v === 'all' ? 'All Departments' : formatDepartmentLabel(v)}
            sx={{
              borderRadius: 1,
              fontSize: '0.8rem',
              height: 28,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.15)
              }
            }}
          >
            <MenuItem value="all">All Departments</MenuItem>
            {VALID_DEPARTMENTS.map(dept => (
              <MenuItem key={dept} value={dept}>
                {formatDepartmentLabel(dept)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {crewMembers.length === 0 ? (
        <Paper 
          elevation={0}
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            px: 3,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.5)
          }}
        >
          <PersonAddIcon sx={{ 
            fontSize: 48, 
            color: 'primary.main', 
            opacity: 0.5,
            mb: 2 
          }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            No Crew Members Yet
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 400 }}>
            Start building your production team by adding crew members.
          </Typography>
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => setOpenCrewDialog(true)}
            sx={{
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            Add Your First Crew Member
          </Button>
        </Paper>
      ) : (
        <>
          {/* Display filtered count */}
          {departmentFilter !== 'all' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Showing {crewMembers.filter(member => member.department === departmentFilter).length} crew members in the {departmentFilter.charAt(0).toUpperCase() + departmentFilter.slice(1).replace('-', ' ')} department
              </Typography>
            </Box>
          )}
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(350px, 1fr))'
            },
            gap: 1,
            p: 0,
            m: 0
          }}>
            {crewMembers
              .filter(member => departmentFilter === 'all' || member.department === departmentFilter)
              .map(member => (
                <CrewCard
                  key={member._id}
                  crewMember={member}
                  onDelete={() => {
                    handleDeleteCrewMember(member._id, member);
                  }}
                  onToggleAdmin={handleToggleAdmin}
                  onEdit={handleUpdateCrewMember}
                  onManagePermissions={handleManagePermissions}
                  currentUserIsAdmin={true}
                  isProjectOwner={isOwner(member)}
                  isLastAdmin={(() => {
                    const adminCount = crewMembers.reduce((acc, m) => {
                      if (m.status === 'pending') return acc; // exclude pending
                      return acc + (effectiveIsAdmin(m) ? 1 : 0);
                    }, 0);
                    return (member.status !== 'pending') && effectiveIsAdmin(member) && adminCount <= 1;
                  })()}
                  effectiveIsAdmin={effectiveIsAdmin(member)}
                />
              ))}
          </Box>
          
          {/* Show message when no results match filter */}
          {departmentFilter !== 'all' && crewMembers.filter(member => member.department === departmentFilter).length === 0 && (
            <Paper 
              elevation={0}
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 4,
                px: 3,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
                textAlign: 'center',
                mt: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                No crew members found in the {departmentFilter.charAt(0).toUpperCase() + departmentFilter.slice(1).replace('-', ' ')} department.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setDepartmentFilter('all')}
              >
                Show All Departments
              </Button>
            </Paper>
          )}
        </>
      )}

      <Dialog 
        open={openCrewDialog} 
        onClose={() => handleCloseDialog()} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Crew Member
            </Typography>
          </Box>
          <IconButton 
            onClick={() => handleCloseDialog()} 
            size="small"
            sx={{ 
              borderRadius: 1,
              color: theme.palette.text.secondary
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Tabs
            value={dialogTab}
            onChange={(e, newValue) => {
              setDialogTab(newValue);
              setSelectedUser(null);
              setNewCrewMember({
                name: '',
                email: '',
                role: '',
                department: ''
              });
              setFormErrors({});
            }}
            sx={{ 
              mb: 3,
              '& .MuiTab-root': {
                minHeight: 48,
                borderRadius: 1,
                mx: 0.5
              },
              '& .MuiTabs-indicator': {
                height: 2
              }
            }}
          >
            <Tab
              icon={<PersonAddIcon />}
              label="Existing User"
              iconPosition="start"
            />
            <Tab
              icon={<EmailIcon />}
              label="New User"
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {dialogTab === 0 ? (
              <Autocomplete
                fullWidth
                options={searchResults}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={selectedUser}
                onChange={(event, newValue) => setSelectedUser(newValue)}
                onInputChange={(event, value) => {
                  setSearchTerm(value);
                  handleSearch(value);
                }}
                loading={isSearching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Users"
                    placeholder="Type a name or email to search"
                    error={!!formErrors.user}
                    helperText={formErrors.user}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {isSearching ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  sx: { maxHeight: 250, overflow: 'auto' },
                }}
                noOptionsText={
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No users found. Try a different search term or invite a new user.
                    </Typography>
                  </Box>
                }
              />
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  placeholder="Full name of crew member"
                  value={newCrewMember.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  placeholder="email@example.com"
                  type="email"
                  value={newCrewMember.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
              </>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3,
              }}
            >
              <FormControl
                fullWidth
                error={!!formErrors.department}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              >
                <InputLabel>Department</InputLabel>
                <Select
                  value={newCrewMember.department}
                  onChange={handleDepartmentChange}
                  label="Department"
                  startAdornment={
                    <InputAdornment position="start" sx={{ ml: 1 }}>
                      <CategoryIcon color="action" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="" disabled>
                    <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Select Department
                    </Box>
                  </MenuItem>
                  {Object.keys(DEPARTMENT_ROLES).map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.department && (
                  <FormHelperText error sx={{ mx: 0 }}>
                    {formErrors.department}
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl
                fullWidth
                error={!!formErrors.role}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              >
                <InputLabel>Role</InputLabel>
                <Select
                  value={newCrewMember.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  label="Role"
                  disabled={!newCrewMember.department}
                  startAdornment={
                    <InputAdornment position="start" sx={{ ml: 1 }}>
                      <WorkIcon color="action" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="" disabled>
                    <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {!newCrewMember.department ? 'Select Department First' : 'Select Role'}
                    </Box>
                  </MenuItem>
                  {availableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role && (
                  <FormHelperText error sx={{ mx: 0 }}>
                    {formErrors.role}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            {dialogTab === 1 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.1)}`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #2196F3, #1976D2)',
                    opacity: 0.7
                  }
                }}
              >
                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon fontSize="small" color="info" />
                  An invitation email will be sent to this address with instructions to join the project.
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => handleCloseDialog()}
            variant="outlined"
            sx={{
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={dialogTab === 0 ? handleAddExistingUser : handleAddCrewMember}
            disabled={isSubmitting}
            sx={{ 
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            {isSubmitting ? 'Adding...' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Permissions Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => { setPermissionsDialogOpen(false); setSelectedMemberForPerms(null); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <AdminPanelSettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Role & Permissions
              </Typography>
              {selectedMemberForPerms?.user?.name && (
                <Typography variant="body2" color="text.secondary">
                  {selectedMemberForPerms.user.name} • {selectedMemberForPerms.user.email}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {/* Admin Toggle Card */}
          <Card 
            sx={{ 
              mb: 3,
              border: '2px solid',
              borderColor: adminDraft ? 'error.main' : 'divider',
              backgroundColor: adminDraft ? alpha(theme.palette.error.main, 0.04) : 'background.paper',
              transition: 'all 0.3s'
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: adminDraft ? 'error.main' : alpha(theme.palette.error.main, 0.1),
                      color: adminDraft ? 'white' : 'error.main'
                    }}
                  >
                    <AdminPanelSettingsIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Admin Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Grant full access to all project features and settings
                    </Typography>
                  </Box>
                </Stack>
                <Switch
                  checked={Boolean(adminDraft)}
                  onChange={(e) => setAdminDraft(e.target.checked)}
                  disabled={selectedMemberForPerms ? isOwner(selectedMemberForPerms) : false}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'error.main',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'error.main',
                    },
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Role Templates */}
          {roleTemplates && Object.keys(roleTemplates).filter((key) => key !== 'owner' && key !== 'admin').length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                QUICK TEMPLATES
              </Typography>
              <Grid container spacing={1.5}>
                {Object.keys(roleTemplates).filter((key) => key !== 'owner' && key !== 'admin').map((key) => (
                  <Grid item xs={6} sm={4} md={3} key={key}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => applyRoleTemplate(key)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack spacing={1} alignItems="center">
                          <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', textTransform: 'capitalize' }}>
                            {key}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search permissions…"
            value={permSearch}
            onChange={(e) => setPermSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ mb: 3 }}
          />

          {/* Permission Groups */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            DETAILED PERMISSIONS
          </Typography>
          <Grid container spacing={2}>
            {permissionsRegistry?.groups?.map((grp) => {
              const groupPerms = (permissionsRegistry.permissions || []).filter(p => p.group === grp.key)
                .filter(p => {
                  if (!permSearch) return true;
                  const s = permSearch.toLowerCase();
                  return (p.label || '').toLowerCase().includes(s) || p.key.toLowerCase().includes(s);
                });
              if (groupPerms.length === 0) return null;
              
              const allOn = groupPerms.every(p => Boolean(permsDraft?.[p.key]));
              const someOn = !allOn && groupPerms.some(p => Boolean(permsDraft?.[p.key]));
              const enabledCount = groupPerms.filter(p => Boolean(permsDraft?.[p.key])).length;
              
              // Icon mapping for groups
              const getGroupIcon = (key) => {
                const iconMap = {
                  'project': <FolderIcon />,
                  'crew': <GroupIcon />,
                  'schedule': <CalendarTodayIcon />,
                  'documents': <DescriptionIcon />,
                  'settings': <SettingsIcon />,
                  'view': <VisibilityIcon />,
                  'edit': <EditIcon />,
                  'create': <CreateIcon />
                };
                return iconMap[key] || <SettingsIcon />;
              };

              return (
                <Grid item xs={12} md={6} key={grp.key}>
                  <Card
                    sx={{
                      height: '100%',
                      border: '1px solid',
                      borderColor: allOn ? 'success.main' : someOn ? 'warning.main' : 'divider',
                      backgroundColor: allOn ? alpha(theme.palette.success.main, 0.02) : 'background.paper'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      {/* Group Header */}
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: allOn ? 'success.main' : someOn ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
                            color: allOn ? 'white' : someOn ? 'warning.main' : 'primary.main',
                            transition: 'all 0.3s'
                          }}
                        >
                          {getGroupIcon(grp.key)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {grp.label || grp.key}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {enabledCount} of {groupPerms.length} enabled
                          </Typography>
                        </Box>
                        {enabledCount > 0 && (
                          <Chip 
                            label={enabledCount}
                            size="small"
                            sx={{
                              backgroundColor: allOn ? 'success.main' : 'warning.main',
                              color: 'white',
                              fontWeight: 600,
                              minWidth: 28,
                              height: 24
                            }}
                          />
                        )}
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      {/* Individual Permissions */}
                      <Stack spacing={1.5}>
                        {groupPerms.map((perm) => (
                          <Box
                            key={perm.key}
                            sx={{
                              p: 1.5,
                              borderRadius: 1.5,
                              backgroundColor: permsDraft?.[perm.key] ? alpha(theme.palette.success.main, 0.08) : alpha(theme.palette.action.hover, 0.02),
                              border: '1px solid',
                              borderColor: permsDraft?.[perm.key] ? 'success.main' : 'divider',
                              transition: 'all 0.2s',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                borderColor: 'primary.main'
                              }
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(permsDraft?.[perm.key])}
                                  onChange={(e) => togglePermDraft(perm.key, e.target.checked)}
                                  size="small"
                                  sx={{
                                    color: permsDraft?.[perm.key] ? 'success.main' : 'text.secondary',
                                    '&.Mui-checked': {
                                      color: 'success.main'
                                    }
                                  }}
                                />
                              }
                              label={
                                <Box sx={{ ml: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {perm.label || perm.key}
                                  </Typography>
                                  {perm.description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      {perm.description}
                                    </Typography>
                                  )}
                                  {Array.isArray(perm.requires) && perm.requires.length > 0 && (
                                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
                                      Requires: {perm.requires.join(', ')}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{ 
                                m: 0,
                                width: '100%',
                                alignItems: 'flex-start'
                              }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {(!permissionsRegistry.permissions || permissionsRegistry.permissions.length === 0) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No permissions registry loaded.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={() => { setPermissionsDialogOpen(false); setSelectedMemberForPerms(null); }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={saveMemberPermissions} 
            disabled={!selectedMemberForPerms}
            startIcon={<CheckCircleIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewManagement;
